<template>
    <div class="display-show-template" :class="theme">
        <vue-snotify />
        <backstretch v-if="show.id.slug" :slug="show.id.slug" :key="show.id.slug" />
        <input type="hidden" id="series-id" value="">
        <input type="hidden" id="indexer-name" value="">
        <input type="hidden" id="series-slug" value="">

        <show-header
            type="show"
            ref="show-header"
            :key="`show-header-${showSlug}`"
            @reflow="reflowLayout"
            :slug="showSlug"
            @update="statusQualityUpdate"
            @update-overview-status="filterByOverviewStatus = $event"
        />

        <div class="row">
            <div class="col-md-12 top-15 displayShow horizontal-scroll">
                <!-- Display non-special episodes, paginate if enabled -->
                <vue-good-table v-if="show.seasons"
                                :columns="columns"
                                :rows="orderedSeasons"
                                :groupOptions="{
                                    enabled: true,
                                    mode: 'span',
                                }"
                                :pagination-options="{
                                    enabled: layout.show.pagination.enable,
                                    perPage: paginationPerPage,
                                    perPageDropdown
                                }"
                                :search-options="{
                                    enabled: true,
                                    trigger: 'enter',
                                    skipDiacritics: false,
                                    placeholder: 'Search episodes',
                                }"
                                :sort-options="{
                                    enabled: true,
                                    initialSortBy: getSortBy('episode', 'desc')
                                }"
                                :selectOptions="{
                                    enabled: true,
                                    selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
                                    selectionInfoClass: 'select-info',
                                    selectionText: 'episodes selected',
                                    clearSelectionText: 'clear',
                                    selectAllByGroup: true
                                }"
                                :row-style-class="rowStyleClassFn"
                                :column-filter-options="{
                                    enabled: true
                                }"
                                ref="table-seasons"
                                @on-selected-rows-change="selectedEpisodes=$event.selectedRows"
                                @on-per-page-change="updatePaginationPerPage($event.currentPerPage)"
                                @on-page-change="onPageChange">

                    <template slot="table-header-row" slot-scope="props">
                        <h3 class="season-header toggle collapse">
                            <app-link :name="'season-'+ props.row.season" />
                            {{ props.row.season > 0 ? 'Season ' + props.row.season : 'Specials' }}
                            <!-- Only show the search manual season search, when any of the episodes in it is not unaired -->
                            <app-link v-if="anyEpisodeNotUnaired(props.row)" class="epManualSearch" :href="`home/snatchSelection?showslug=${show.id.slug}&amp;season=${props.row.season}&amp;episode=1&amp;manual_search_type=season`">
                                <img data-ep-manual-search src="images/manualsearch-white.png" width="16" height="16" alt="search" title="Manual Search">
                            </app-link>
                            <div class="season-scene-exception" :data-season="props.row.season > 0 ? props.row.season : 'Specials'" />
                            <img v-bind="getSeasonExceptions(props.row.season)">
                        </h3>
                    </template>

                    <template slot="table-footer-row" slot-scope="{headerRow}">
                        <tr colspan="9999" :id="`season-${headerRow.season}-footer`" class="seasoncols border-bottom shadow">
                            <th class="col-footer" colspan="15">Season contains {{headerRow.children.length}} episodes with total filesize: {{addFileSize(headerRow)}}</th>
                        </tr>
                        <tr class="spacer" />
                    </template>

                    <template slot="table-row" slot-scope="props">
                        <span v-if="props.column.field == 'content.hasNfo'">
                            <img :src="`images/${props.row.content.hasNfo ? 'nfo.gif' : 'nfo-no.gif'}`" :alt="(props.row.content.hasNfo ? 'Y' : 'N')" width="23" height="11">
                        </span>
                        <span v-else-if="props.column.field == 'content.hasTbn'">
                            <img :src="`images/${props.row.content.hasTbn ? 'tbn.gif' : 'tbn-no.gif'}`" :alt="(props.row.content.hasTbn ? 'Y' : 'N')" width="23" height="11">
                        </span>

                        <span v-else-if="props.column.label == 'Episode'">
                            <span :title="props.row.file.location !== '' ? props.row.file.location : ''" :class="{addQTip: props.row.file.location !== ''}">{{props.row.episode}}</span>
                        </span>

                        <span v-else-if="props.column.label == 'Scene'">
                            <div class="align-center">
                                <scene-number-input :show="show" :initial-episode="props.row" />
                            </div>
                        </span>

                        <span v-else-if="props.column.label == 'Scene Abs. #'">
                            <div class="align-center">
                                <scene-number-anime-input :show="show" :initial-episode="props.row" />
                            </div>
                        </span>

                        <span v-else-if="props.column.label == 'Title'">
                            <plot-info v-if="props.row.description !== ''" :description="props.row.description" :show-slug="show.id.slug" :season="props.row.season" :episode="props.row.episode" />
                            {{props.row.title}}
                        </span>

                        <span v-else-if="props.column.label == 'File'">
                            <span :title="props.row.file.location" class="addQTip">{{props.row.file.name}}</span>
                        </span>

                        <span v-else-if="props.column.label == 'Subtitles'">
                            <div class="subtitles align-center">
                                <div v-for="flag in props.row.subtitles" :key="flag">
                                    <img
                                        v-if="flag !== 'und'" :src="`images/subtitles/flags/${flag}.png`"
                                        width="16" height="11" :alt="flag" class="addQTip" :title="flag"
                                        onError="this.onerror=null;this.src='images/flags/unknown.png';"
                                        @click="searchSubtitle($event, props.row, flag)"
                                    >
                                    <img v-else :src="`images/subtitles/flags/${flag}.png`" class="subtitle-flag" width="16" height="11" :alt="flag" onError="this.onerror=null;this.src='images/flags/unknown.png';">
                                </div>
                            </div>
                        </span>

                        <span v-else-if="props.column.label == 'Status'">
                            <div class="pull-left">
                                <quality-pill v-if="props.row.quality !== 0" :quality="props.row.quality" />
                                {{props.row.status}}
                                <img v-if="props.row.status !== 'Unaired'"
                                     :title="props.row.watched ? 'This episode has been flagged as watched' : ''"
                                     class="addQTip" :src="`images/${props.row.watched ? '' : 'not'}watched.png`"
                                     width="16" height="16" style="margin-left: 5px" @click="updateEpisodeWatched(props.row, !props.row.watched);"
                                >
                            </div>
                        </span>

                        <span v-else-if="props.column.field == 'search'">
                            <div class="full-width">
                                <search v-if="props.row.slug" :ref="`search-${props.row.slug}`" style="margin-right: 0.25rem" searchType="backlog" :showSlug="showSlug" :episode="{
                                    episode: props.row.episode, season: props.row.season, slug: props.row.slug
                                }" />

                                <search v-if="props.row.slug" style="margin-right: 0.25rem" searchType="manual" :showSlug="showSlug" :episode="{
                                    episode: props.row.episode, season: props.row.season, slug: props.row.slug
                                }" />
                                <img src="images/closed_captioning.png" height="16" alt="search subtitles" title="Search Subtitles" @click="searchSubtitle($event, props.row)">
                            </div>
                            <div class="mobile">
                                <select name="search-select" class="form-control input-sm mobile-select" @change="mobileSelectSearch($event, props.row)">
                                    <option disabled selected value="search action">search action</option>
                                    <option value="forced">{{retryDownload(props.row) ? 'retry' : 'search'}}</option>
                                    <option value="manual">manual</option>
                                    <option value="subtitle">subtitle</option>
                                </select>
                            </div>
                        </span>

                        <span v-else>
                            {{props.formattedRow[props.column.field]}}
                        </span>
                    </template>

                    <template slot="table-column" slot-scope="props">
                        <span v-if="props.column.label =='Abs. #'">
                            <span title="Absolute episode number" class="addQTip">{{props.column.label}}</span>
                        </span>
                        <span v-else-if="props.column.label =='Scene Abs. #'">
                            <span title="Scene Absolute episode number" class="addQTip">{{props.column.label}}</span>
                        </span>
                        <span v-else>
                            {{props.column.label}}
                        </span>
                    </template>

                </vue-good-table>

                <!-- Display special episodes -->
                <vue-good-table v-if="layout.show.specials && specials && specials.length > 0"
                                :columns="columns"
                                :rows="specials"
                                :groupOptions="{
                                    enabled: true,
                                    mode: 'span',
                                    customChildObject: 'episodes'
                                }"
                                :pagination-options="{
                                    enabled: false
                                }"
                                :search-options="{
                                    enabled: true,
                                    trigger: 'enter',
                                    skipDiacritics: false,
                                    placeholder: 'Search specials',
                                }"
                                :sort-options="{
                                    enabled: true,
                                    multipleColumns: true,
                                    initialSortBy: getSortBy('episode', 'desc') // From mixin manage-cookie.js
                                }"
                                :selectOptions="{
                                    enabled: true,
                                    selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
                                    selectionInfoClass: 'select-info',
                                    selectionText: 'episodes selected',
                                    clearSelectionText: 'clear',
                                    selectAllByGroup: true
                                }"
                                :row-style-class="rowStyleClassFn"
                                :column-filter-options="{
                                    enabled: false
                                }"
                                ref="table-specials"
                                @on-selected-rows-change="selectedEpisodes=$event.selectedRows">

                    <template slot="table-header-row" slot-scope="props">
                        <h3 class="season-header toggle collapse"><app-link :name="'season-'+ props.row.season" />
                            {{ props.row.season > 0 ? 'Season ' + props.row.season : 'Specials' }}
                            <!-- Only show the search manual season search, when any of the episodes in it is not unaired -->
                            <app-link v-if="anyEpisodeNotUnaired(props.row)" class="epManualSearch" :href="`home/snatchSelection?showslug=${show.id.slug}&amp;season=${props.row.season}&amp;episode=1&amp;manual_search_type=season`">
                                <img data-ep-manual-search src="images/manualsearch-white.png" width="16" height="16" alt="search" title="Manual Search">
                            </app-link>
                            <div class="season-scene-exception" :data-season="props.row.season > 0 ? props.row.season : 'Specials'" />
                            <img v-bind="getSeasonExceptions(props.row.season)">
                        </h3>
                    </template>

                    <template slot="table-footer-row" slot-scope="{headerRow}">
                        <tr colspan="9999" :id="`season-${headerRow.season}-footer`" class="seasoncols border-bottom shadow">
                            <th class="col-footer" colspan="15" align="left">Season contains {{headerRow.children.length}} episodes with total filesize: {{addFileSize(headerRow)}}</th>
                        </tr>
                        <tr class="spacer" />
                    </template>

                    <template slot="table-row" slot-scope="props">
                        <span v-if="props.column.field == 'content.hasNfo'">
                            <img :src="`images/${props.row.content.hasNfo ? 'nfo.gif' : 'nfo-no.gif'}`" :alt="(props.row.content.hasNfo ? 'Y' : 'N')" width="23" height="11">
                        </span>
                        <span v-else-if="props.column.field == 'content.hasTbn'">
                            <img :src="`images/${props.row.content.hasTbn ? 'tbn.gif' : 'tbn-no.gif'}`" :alt="(props.row.content.hasTbn ? 'Y' : 'N')" width="23" height="11">
                        </span>

                        <span v-else-if="props.column.label == 'Episode'">
                            <span :title="props.row.file.location !== '' ? props.row.file.location : ''" :class="{addQTip: props.row.file.location !== ''}">{{props.row.episode}}</span>
                        </span>

                        <span v-else-if="props.column.label == 'Scene'">
                            <div class="align-center">
                                <scene-number-input :show="show" :initial-episode="props.row" />
                            </div>
                        </span>

                        <span v-else-if="props.column.label == 'Scene Abs. #'">
                            <div class="align-center">
                                <scene-number-anime-input :show="show" :initial-episode="props.row" />
                            </div>
                        </span>

                        <span v-else-if="props.column.label == 'Title'">
                            <plot-info v-if="props.row.description !== ''" :description="props.row.description" :show-slug="show.id.slug" :season="props.row.season" :episode="props.row.episode" />
                            {{props.row.title}}
                        </span>

                        <span v-else-if="props.column.label == 'File'">
                            <span :title="props.row.file.location" class="addQTip">{{props.row.file.name}}</span>
                        </span>

                        <span v-else-if="props.column.label == 'Subtitles'">
                            <div class="subtitles align-center">
                                <div v-for="flag in props.row.subtitles" :key="flag">
                                    <img
                                        v-if="flag !== 'und'" :src="`images/subtitles/flags/${flag}.png`"
                                        width="16" height="11" :alt="flag" class="addQTip" :title="flag"
                                        onError="this.onerror=null;this.src='images/flags/unknown.png';"
                                        @click="searchSubtitle($event, props.row, flag)"
                                    >
                                    <img v-else :src="`images/subtitles/flags/${flag}.png`" class="subtitle-flag" width="16" height="11" alt="flag" onError="this.onerror=null;this.src='images/flags/unknown.png';">
                                </div>
                            </div>
                        </span>

                        <span v-else-if="props.column.label == 'Status'">
                            <div class="pull-left align-center">
                                {{props.row.status}}
                                <quality-pill v-if="props.row.quality !== 0" :quality="props.row.quality" class="quality-margins" />
                                <img :title="props.row.watched ? 'This episode has been flagged as watched' : ''" class="addQTip" v-if="props.row.status !== 'Unaired'" :src="`images/${props.row.watched ? '' : 'not'}watched.png`" width="16" @click="updateEpisodeWatched(props.row, !props.row.watched);">
                            </div>
                        </span>

                        <span v-else-if="props.column.field == 'search'">
                            <div class="full-width">
                                <search v-if="props.row.slug" :ref="`search-${props.row.slug}`" style="margin-right: 0.25rem" searchType="backlog" :showSlug="showSlug" :episode="{
                                    episode: props.row.episode, season: props.row.season, slug: props.row.slug
                                }" />

                                <search v-if="props.row.slug" style="margin-right: 0.25rem" searchType="manual" :showSlug="showSlug" :episode="{
                                    episode: props.row.episode, season: props.row.season, slug: props.row.slug
                                }" />

                                <img src="images/closed_captioning.png" height="16" alt="search subtitles" title="Search Subtitles" @click="searchSubtitle($event, props.row)">
                            </div>
                            <div class="mobile">
                                <select name="search-select" class="form-control input-sm mobile-select" @change="mobileSelectSearch($event, props.row)">
                                    <option disabled selected value="search action">search action</option>
                                    <option value="forced">{{retryDownload(props.row) ? 'retry' : 'search'}}</option>
                                    <option value="manual">manual</option>
                                    <option value="subtitle">subtitle</option>
                                </select>
                            </div>
                        </span>

                        <span v-else>
                            {{props.formattedRow[props.column.field]}}
                        </span>
                    </template>

                    <template slot="table-column" slot-scope="props">
                        <span v-if="props.column.label =='Abs. #'">
                            <span title="Absolute episode number" class="addQTip">{{props.column.label}}</span>
                        </span>
                        <span v-else-if="props.column.label =='Scene Abs. #'">
                            <span title="Scene Absolute episode number" class="addQTip">{{props.column.label}}</span>
                        </span>
                        <span v-else>
                            {{props.column.label}}
                        </span>
                    </template>

                </vue-good-table>
            </div>
        </div>

        <!-- eslint-disable @sharkykh/vue-extra/component-not-registered -->
        <modal name="query-start-backlog-search" @before-open="beforeBacklogSearchModalClose" :height="'auto'" :width="'80%'">
            <transition name="modal">
                <div class="modal-mask">
                    <div class="modal-wrapper">
                        <div class="modal-content">
                            <div class="modal-header">
                                <button type="button" class="close" data-dismiss="modal" aria-hidden="true">&times;</button>
                                <h4 class="modal-title">Start search?</h4>
                            </div>
                            <div class="modal-body">
                                <p>Some episodes have been changed to 'Wanted'. Do you want to trigger a backlog search for these {{backlogSearchEpisodes.length}} episode(s)</p>
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn-medusa btn-danger" data-dismiss="modal" @click="$modal.hide('query-start-backlog-search')">No</button>
                                <button type="button" class="btn-medusa btn-success" data-dismiss="modal" @click="search(backlogSearchEpisodes, 'backlog'); $modal.hide('query-start-backlog-search')">Yes</button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </modal>

        <modal name="query-mark-failed-and-search" @before-open="beforeFailedSearchModalClose" :height="'auto'" :width="'80%'">
            <transition name="modal">
                <div class="modal-mask">
                    <div class="modal-wrapper">
                        <div class="modal-content">
                            <div class="modal-header">
                                Mark episode as failed and search?
                            </div>

                            <div class="modal-body">
                                <p>Starting to search for the episode</p>
                                <p v-if="failedSearchEpisodes && failedSearchEpisodes.length === 1">Would you also like to mark episode {{failedSearchEpisodes[0].slug}} as "failed"? This will make sure the episode cannot be downloaded again</p>
                                <p v-else-if="failedSearchEpisodes">Would you also like to mark episodes {{failedSearchEpisodes.map(ep => ep.slug).join(', ')}} as "failed"? This will make sure the episode cannot be downloaded again</p>
                            </div>

                            <div class="modal-footer">
                                <button type="button" class="btn-medusa btn-danger" data-dismiss="modal" @click="search(failedSearchEpisodes, 'backlog'); $modal.hide('query-mark-failed-and-search')">No</button>
                                <button type="button" class="btn-medusa btn-success" data-dismiss="modal" @click="search(failedSearchEpisodes, 'failed'); $modal.hide('query-mark-failed-and-search')">Yes</button>
                                <button type="button" class="btn-medusa btn-danger" data-dismiss="modal" @click="$modal.hide('query-mark-failed-and-search')">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </modal>
        <!-- eslint-enable -->
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import { AppLink, PlotInfo, Search, SceneNumberInput, SceneNumberAnimeInput } from './helpers';
import { humanFileSize } from '../utils/core';
import { manageCookieMixin } from '../mixins/manage-cookie';
import { addQTip } from '../utils/jquery';
import { VueGoodTable } from 'vue-good-table';
import Backstretch from './backstretch.vue';
import ShowHeader from './show-header.vue';
import SubtitleSearch from './subtitle-search.vue';
import QualityPill from './helpers/quality-pill.vue';

export default {
    name: 'show',
    components: {
        AppLink,
        Backstretch,
        PlotInfo,
        QualityPill,
        Search,
        SceneNumberInput,
        SceneNumberAnimeInput,
        ShowHeader,
        VueGoodTable
    },
    mixins: [
        manageCookieMixin('displayShow')
    ],
    metaInfo() {
        if (!this.show || !this.show.title) {
            return {
                title: 'Medusa'
            };
        }

        const { title } = this.show;
        return {
            title,
            titleTemplate: '%s | Medusa'
        };
    },
    props: {
        /**
         * Show Slug
         */
        slug: {
            type: String
        }
    },
    data() {
        const { getCookie } = this;
        const perPageDropdown = [25, 50, 100, 250, 500];
        const getPaginationPerPage = () => {
            const rows = getCookie('pagination-perPage');
            if (!rows) {
                return 50;
            }

            if (!perPageDropdown.includes(rows)) {
                return 500;
            }
            return rows;
        };
        return {
            invertTable: true,
            subtitleSearchComponents: [],
            columns: [{
                label: 'NFO',
                field: 'content.hasNfo',
                type: 'boolean',
                sortable: false,
                hidden: getCookie('NFO')
            }, {
                label: 'TBN',
                field: 'content.hasTbn',
                type: 'boolean',
                sortable: false,
                hidden: getCookie('TBN')
            }, {
                label: 'Episode',
                field: 'episode',
                type: 'number',
                hidden: getCookie('Episode')
            }, {
                label: 'Abs. #',
                field: 'absoluteNumber',
                type: 'number',
                hidden: getCookie('Abs. #')
            }, {
                label: 'Scene',
                field: 'scene',
                sortable: false,
                hidden: getCookie('Scene')
            }, {
                label: 'Scene Abs. #',
                field: 'sceneAbsolute',
                type: 'number',
                hidden: getCookie('Scene Abs. #')
            }, {
                label: 'Title',
                field: 'title',
                hidden: getCookie('Title')
            }, {
                label: 'File',
                field: 'file.location',
                hidden: getCookie('File')
            }, {
                label: 'Size',
                field: 'file.size',
                type: 'number',
                formatFn: humanFileSize,
                hidden: getCookie('Size')
            }, {
                // For now i'm using a custom function the parse it. As the type: date, isn't working for us.
                // But the goal is to have this user formatted (as configured in backend)
                label: 'Air date',
                field: this.parseDateFn,
                tdClass: 'align-center-span',
                sortable: false,
                hidden: getCookie('Air date')
            }, {
                label: 'Subtitles',
                field: 'subtitles',
                sortable: false,
                hidden: getCookie('Subtitles')
            }, {
                label: 'Status',
                field: 'status',
                hidden: getCookie('Status')
            }, {
                label: 'Search',
                field: 'search',
                sortable: false,
                hidden: getCookie('Search')
            }],
            perPageDropdown,
            paginationPerPage: getPaginationPerPage(),
            selectedEpisodes: [],
            // We need to keep track of which episode where trying to search, for the vue-modal
            failedSearchEpisodes: [],
            backlogSearchEpisodes: [],
            filterByOverviewStatus: false,
            selectedSearch: 'search action'
        };
    },
    computed: {
        ...mapState({
            shows: state => state.shows.shows,
            subtitles: state => state.config.subtitles,
            configLoaded: state => state.config.system.configLoaded,
            layout: state => state.config.layout,
            stateSearch: state => state.config.search,
            client: state => state.auth.client
        }),
        ...mapGetters({
            show: 'getCurrentShow',
            getOverviewStatus: 'getOverviewStatus',
            fuzzyParseDateTime: 'fuzzyParseDateTime'
        }),
        showSlug() {
            const { slug } = this;
            return slug || this.$route.query.showslug;
        },
        theme() {
            const { layout } = this;
            const { themeName } = layout;
            return themeName || 'light';
        },
        orderedSeasons() {
            const { filterByOverviewStatus, invertTable, show } = this;

            if (!show.seasons) {
                return [];
            }

            const seasons = show.seasons.slice();

            let sortedSeasons = seasons.sort((a, b) => a.season - b.season).filter(season => season.season !== 0);

            // Use the filterOverviewStatus to filter the data based on what's checked in the show-header.
            if (filterByOverviewStatus && filterByOverviewStatus.filter(status => status.checked).length < filterByOverviewStatus.length) {
                const filteredSortedSeasons = [];
                for (const season of sortedSeasons) {
                    const { children, ...res } = season;
                    const filteredEpisodes = children.filter(episode => {
                        const episodeOverviewStatus = this.getOverviewStatus(episode.status, episode.quality, show.config.qualities);
                        const filteredStatus = filterByOverviewStatus.find(overviewStatus => overviewStatus.name === episodeOverviewStatus);
                        return !filteredStatus || filteredStatus.checked;
                    });
                    filteredSortedSeasons.push(Object.assign({
                        children: filteredEpisodes
                    }, res));
                }
                sortedSeasons = filteredSortedSeasons;
            }

            if (invertTable) {
                return sortedSeasons.slice().reverse();
            }

            return sortedSeasons;
        },
        specials() {
            const { show } = this;
            if (!show.seasons) {
                return [];
            }
            return show.seasons.filter(season => season.season === 0);
        }
    },

    mounted() {
        this.loadShow();
        ['load', 'resize'].map(event => {
            return window.addEventListener(event, () => {
                this.reflowLayout();
            });
        });

        $(document.body).on('click', '.seasonCheck', event => {
            const seasCheck = event.currentTarget;
            const seasNo = $(seasCheck).attr('id');

            $('#collapseSeason-' + seasNo).collapse('show');
            const seasonIdentifier = 's' + seasNo;
            $('.epCheck:visible').each((index, element) => {
                const epParts = $(element).attr('id').split('e');
                if (epParts[0] === seasonIdentifier) {
                    element.checked = seasCheck.checked;
                }
            });
        });
    },
    methods: {
        humanFileSize,
        ...mapActions({
            getShow: 'getShow', // Map `this.getShow()` to `this.$store.dispatch('getShow')`
            getEpisodes: 'getEpisodes',
            setCurrentShow: 'setCurrentShow',
            setRecentShow: 'setRecentShow'
        }),
        async loadShow() {
            const { setCurrentShow, showSlug, initializeEpisodes, getShow } = this;
            // We need detailed info for the xem / scene exceptions, so let's get it.
            await getShow({ showSlug, detailed: true });

            // Let's tell the store which show we currently want as current.
            // Run this after getShow(), as it will trigger the initializeEpisodes() method.
            setCurrentShow(showSlug);

            // Load all episodes
            initializeEpisodes();
        },
        statusQualityUpdate(event) {
            const { selectedEpisodes, setStatus, setQuality } = this;

            if (event.newQuality !== null && event.newQuality !== 'Change quality to:') {
                setQuality(event.newQuality, selectedEpisodes);
            }

            if (event.newStatus !== null && event.newStatus !== 'Change status to:') {
                setStatus(event.newStatus, selectedEpisodes);
            }
        },
        setQuality(quality, episodes) {
            const { showSlug, getEpisodes, show } = this;
            const patchData = {};

            episodes.forEach(episode => {
                patchData[episode.slug] = { quality: Number.parseInt(quality, 10) };
            });

            this.client.api.patch(`series/${show.id.slug}/episodes`, patchData) // eslint-disable-line no-undef
                .then(_ => {
                    console.info(`patched show ${show.id.slug} with quality ${quality}`);
                    [...new Set(episodes.map(episode => episode.season))].forEach(season => {
                        getEpisodes({ showSlug, season });
                    });
                }).catch(error => {
                    console.error(String(error));
                });
        },
        setStatus(status, episodes) {
            const { showSlug, getEpisodes } = this;
            const patchData = {};

            episodes.forEach(episode => {
                patchData[episode.slug] = { status };
            });

            this.client.api.patch(`series/${showSlug}/episodes`, patchData) // eslint-disable-line no-undef
                .then(_ => {
                    console.info(`patched show ${showSlug} with status ${status}`);
                    [...new Set(episodes.map(episode => episode.season))].forEach(season => {
                        getEpisodes({ showSlug, season });
                    });
                }).catch(error => {
                    console.error(String(error));
                });

            // New status Wanted
            if (status === 3) {
                this.$modal.show('query-start-backlog-search', { episodes });
            }

            // New status Failed
            if (status === 11) {
                this.$modal.show('query-mark-failed-and-search', { episodes });
            }
        },
        parseDateFn(row) {
            const { fuzzyParseDateTime } = this;
            return fuzzyParseDateTime(row.airDate);
        },
        rowStyleClassFn(row) {
            const { getOverviewStatus, show } = this;
            if (Object.keys(row).includes('vgt_header_id')) {
                return;
            }
            const overview = getOverviewStatus(row.status, row.quality, show.config.qualities).toLowerCase().trim();
            return overview;
        },
        /**
         * Add (reduce) the total episodes filesize.
         * @param {object} headerRow header row object.
         * @returns {string} - Human readable file size.
         */
        addFileSize(headerRow) {
            return humanFileSize(headerRow.children.reduce((a, b) => a + (b.file.size || 0), 0));
        },
        searchSubtitle(event, episode, lang) {
            const { showSlug, getEpisodes, show, subtitleSearchComponents } = this;
            const SubtitleSearchClass = Vue.extend(SubtitleSearch); // eslint-disable-line no-undef
            const instance = new SubtitleSearchClass({
                propsData: { show, episode, key: episode.originalIndex, lang },
                parent: this
            });

            // Update the show, as we downloaded new subtitle(s)
            instance.$on('update', event => {
                // This could be replaced by the generic websocket updates in future.
                if (event.reason === 'new subtitles found') {
                    getEpisodes({ showSlug, season: episode.season });
                }
            });

            const node = document.createElement('div');
            const tableRef = episode.season === 0 ? 'table-specials' : 'table-seasons';
            this.$refs[tableRef].$refs[`row-${episode.originalIndex}`][0].after(node);
            instance.$mount(node);
            subtitleSearchComponents.push(instance);
        },

        /**
         * Attaches IMDB tooltip
         */
        reflowLayout: debounce(() => {
            console.debug('Reflowing layout');
            addQTip(); // eslint-disable-line no-undef
        }, 1000),
        /**
         * Check if any of the episodes in this season does not have the status "unaired".
         * If that's the case we want to manual season search icon.
         * @param {object} season - A season object.
         * @returns {Boolean} - true if one of the seasons episodes has a status 'unaired'.
         */
        anyEpisodeNotUnaired(season) {
            return season.children.filter(ep => ep.status !== 'Unaired').length > 0;
        },
        episodesInverse(season) {
            const { invertTable } = this;
            if (!season.children) {
                return [];
            }

            if (invertTable) {
                return season.children.slice().reverse();
            }

            return season.children;
        },
        /**
         * Vue-js-modal requires a method, to pass an event to.
         * The event then can be used to assign the value of the episode.
         * @param {Object} event - vue js modal event
         */
        beforeBacklogSearchModalClose(event) {
            this.backlogSearchEpisodes = event.params.episodes;
        },
        /**
         * Vue-js-modal requires a method, to pass an event to.
         * The event then can be used to assign the value of the episode.
         * @param {Object} event - vue js modal event
         */
        beforeFailedSearchModalClose(event) {
            this.failedSearchEpisodes = event.params.episodes;
        },
        retryDownload(episode) {
            const { stateSearch } = this;
            return (stateSearch.general.failedDownloads.enabled && ['Snatched', 'Snatched (Proper)', 'Snatched (Best)', 'Downloaded'].includes(episode.status));
        },
        search(episodes, searchType) {
            const { show } = this;
            let data = {};

            if (episodes) {
                data = {
                    showSlug: show.id.slug,
                    episodes: [],
                    options: {}
                };
                episodes.forEach(episode => {
                    data.episodes.push(episode.slug);
                    this.$refs[`search-${episode.slug}`].src = 'images/loading16.gif';
                });
            }

            this.client.api.put(`search/${searchType}`, data) // eslint-disable-line no-undef
                .then(_ => {
                    if (episodes.length === 1) {
                        console.info(`started search for show: ${show.id.slug} episode: ${episodes[0].slug}`);
                        this.$refs[`search-${episodes[0].slug}`].src = 'images/queued.png';
                        this.$refs[`search-${episodes[0].slug}`].disabled = true;
                    } else {
                        console.info('started a full backlog search');
                    }
                }).catch(error => {
                    console.error(String(error));

                    episodes.forEach(episode => {
                        data.episodes.push(episode.slug);
                        this.$refs[`search-${episodes[0].slug}`].src = 'images/no16.png';
                    });
                }).finally(() => {
                    this.failedSearchEpisodes = [];
                    this.backlogSearchEpisodes = [];
                });
        },
        /**
         * Start a backlog search or failed search for the specific episode.
         * A failed search is started depending on the current episodes status.
         * @param {Object} episode - Episode object. If no episode object is passed, a backlog search is started.
         */
        queueSearch(episode) {
            const { $modal, search, retryDownload } = this;
            const episodeIdentifier = episode.slug;
            if (episode) {
                if (this.$refs[`search-${episodeIdentifier}`].disabled === true) {
                    return;
                }

                if (retryDownload(episode)) {
                    $modal.show('query-mark-failed-and-search', { episode });
                } else {
                    search([episode], 'backlog');
                }
            }
        },
        showSubtitleButton(episode) {
            const { subtitles, show } = this;
            return (episode.season !== 0 && subtitles.enabled && show.config.subtitlesEnabled && !['Snatched', 'Snatched (Proper)', 'Snatched (Best)', 'Downloaded'].includes(episode.status));
        },
        totalSeasonEpisodeSize(season) {
            return season.children.filter(x => x.file && x.file.size > 0).reduce((a, b) => a + b.file.size, 0);
        },
        getSeasonExceptions(season) {
            const { show } = this;
            const { config } = show;
            const { aliases } = config;
            let bindData = { class: 'display: none' };

            // Map the indexer season to a xem mapped season.
            // check if the season exception also exists in the xem numbering table

            let xemSeasons = [];
            let foundInXem = false;
            if (show.xemNumbering.length > 0) {
                const xemResult = show.xemNumbering.filter(x => x.source.season === season);
                // Create an array with unique seasons
                xemSeasons = [...new Set(xemResult.map(item => item.destination.season))];
                foundInXem = Boolean(xemSeasons.length);
            }

            // Check if there is a season exception for this season
            if (aliases.find(x => x.season === season)) {
                // If there is not a match on the xem table, display it as a medusa scene exception
                bindData = {
                    id: `xem-exception-season-${foundInXem ? xemSeasons[0] : season}`,
                    alt: foundInXem ? '[xem]' : '[medusa]',
                    src: foundInXem ? 'images/xem.png' : 'images/ico/favicon-16.png',
                    title: foundInXem ? xemSeasons.reduce((a, b) => {
                        return a.concat(aliases.filter(alias => alias.season === b).map(alias => alias.title));
                    }, []).join(', ') : aliases.filter(alias => alias.season === season).map(alias => alias.title).join(', ')
                };
            }

            return bindData;
        },
        updateEpisodeWatched(episode, watched) {
            const { showSlug, getEpisodes, show } = this;
            const patchData = {};

            patchData[episode.slug] = { watched };

            this.client.api.patch(`series/${show.id.slug}/episodes`, patchData) // eslint-disable-line no-undef
                .then(_ => {
                    console.info(`patched episode ${episode.slug} with watched set to ${watched}`);
                    getEpisodes({ showSlug, season: episode.season });
                }).catch(error => {
                    console.error(String(error));
                });

            episode.watched = watched;
        },
        updatePaginationPerPage(rows) {
            const { setCookie } = this;
            this.paginationPerPage = rows;
            setCookie('pagination-perPage', rows);
        },
        onPageChange(params) {
            if (params.prevPage !== params.currentPage) {
                this.loadEpisodes(params.currentPage);
            }
        },
        neededSeasons(page) {
            const { layout, paginationPerPage, show } = this;
            const { seasonCount } = show;
            if (!seasonCount || seasonCount.length === 0) {
                return [];
            }

            if (!layout.show.pagination.enable) {
                return seasonCount.filter(season => season.season !== 0).map(season => season.season).reverse();
            }

            const seasons = show.seasonCount.length - 1;

            let pagesCount = 1;
            let episodeCount = 0;
            const pages = {};
            for (let i = seasons; i >= 0; i--) {
                const { season } = show.seasonCount[i];
                // Exclude specials
                if (season === 0) {
                    break;
                }

                if (pagesCount in pages) {
                    pages[pagesCount].push(season);
                } else {
                    pages[pagesCount] = [season];
                }

                episodeCount += show.seasonCount[i].episodeCount;
                if (episodeCount / paginationPerPage > pagesCount) {
                    pagesCount++;
                    pages[pagesCount] = [season];
                }

                if (pagesCount > page) {
                    break;
                }
            }
            return pages[page] || [];
        },
        loadEpisodes(page) {
            const { showSlug, getEpisodes } = this;
            // Wrap getEpisodes into an async/await function, so we can wait for the season to have been committed
            // before going on to the next one.
            const _getEpisodes = async showSlug => {
                for (const season of this.neededSeasons(page)) {
                    // We're waiting for the results by design, to give vue the chance to update the dom.
                    // If we fire all the promises at once for, for example 25 seasons. We'll overload medusa's app
                    // and chance is high a number of requests will timeout.
                    await getEpisodes({ showSlug, season }); // eslint-disable-line no-await-in-loop
                }
            };

            _getEpisodes(showSlug);
        },
        initializeEpisodes(force = false) {
            const { getEpisodes, showSlug, setRecentShow, show } = this;
            if (force || (!show.seasons && show.seasonCount)) {
                // Load episodes for the first page.
                this.loadEpisodes(1);
                // Always get special episodes if available.
                if (show.seasonCount.length > 0 && show.seasonCount[0].season === 0) {
                    getEpisodes({ showSlug, season: 0 });
                }
            }

            if (show.id.slug) {
                // For now i'm dumping this here
                setRecentShow({
                    showSlug: show.id.slug,
                    name: show.title
                });
            }
        },
        mobileSelectSearch(event, episode) {
            const { $snotify, $router, queueSearch, searchSubtitle, show } = this;
            if (event.target.value === 'forced') {
                queueSearch(episode);
                $snotify.success(
                    `Search started for S${episode.season} E${episode.episode}`
                );
            }

            if (event.target.value === 'manual') {
                // Use the router to navigate to snatchSelection.
                $router.push({ name: 'snatchSelection', query: {
                    showslug: show.id.slug,
                    season: episode.season,
                    episode: episode.episode
                } });
            }

            if (event.target.value === 'subtitle') {
                searchSubtitle(event, episode);
            }

            setTimeout(() => {
                event.target.value = 'search action';
            }, 2000);
        }
    },
    filters: {
        sceneObjectToString(value) {
            return `${value.season}x${value.episode}`;
        }
    },
    watch: {
        $route(to, from) {
            if (to.name === 'show' && from.name === 'testRename') {
                // Load all episodes if we're coming from the testRename page.
                this.initializeEpisodes(true);
            }
        }
    }
};
</script>

<style scoped>
.defaultTable.displayShow {
    clear: both;
}

.displayShowTable.displayShow {
    clear: both;
}

.fanartBackground.displayShow {
    clear: both;
    opacity: 0.9;
}

.fanartBackground table {
    table-layout: auto;
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    text-align: center;
    border: none;
    empty-cells: show;
    color: rgb(0, 0, 0) !important;
}

.summaryFanArt {
    opacity: 0.9;
}

.fanartBackground > table th.vgt-row-header {
    border: none !important;
    background-color: transparent !important;
    color: rgb(255, 255, 255) !important;
    padding-top: 15px !important;
    text-align: left !important;
}

.fanartBackground td.col-search {
    text-align: center;
}

/* Trying to migrate this from tablesorter */

/* =======================================================================
tablesorter.css
========================================================================== */

.displayShow >>> .vgt-global-search__input.vgt-pull-left {
    float: left;
    height: 40px;
}

.displayShow >>> .vgt-input {
    border: 1px solid #ccc;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
    height: 30px;
    padding: 5px 10px;
    font-size: 12px;
    line-height: 1.5;
    border-radius: 3px;
}

.displayShow >>> div.vgt-responsive > table tbody > tr > th.vgt-row-header > span {
    font-size: 24px;
    margin-top: 20px;
    margin-bottom: 10px;
}

.displayShow >>> .vgt-table {
    width: 100%;
    margin-right: auto;
    margin-left: auto;
    color: rgb(0, 0, 0);
    text-align: left;
    border-spacing: 0;
    border-collapse: initial;
}

.displayShow >>> .vgt-table th,
.displayShow >>> .vgt-table td {
    padding: 4px;
    border-top: rgb(34, 34, 34) 1px solid;
    border-left: rgb(34, 34, 34) 1px solid;
    vertical-align: middle;
}

/* remove extra border from left edge */
.displayShow >>> .vgt-table th:first-child,
.displayShow >>> .vgt-table td:first-child {
    border-left: none;
}

.displayShow >>> .vgt-table span.break-word {
    word-wrap: break-word;
}

.displayShow >>> .vgt-table thead th.sorting.sorting-desc {
    background-color: rgb(85, 85, 85);
    background-image: url(data:image/gif;base64,R0lGODlhFQAEAIAAAP///////yH5BAEAAAEALAAAAAAVAAQAAAINjB+gC+jP2ptn0WskLQA7);
}

.displayShow >>> .vgt-table thead th.sorting.sorting-asc {
    background-color: rgb(85, 85, 85);
    background-image: url(data:image/gif;base64,R0lGODlhFQAEAIAAAP///////yH5BAEAAAEALAAAAAAVAAQAAAINjI8Bya2wnINUMopZAQA7);
    background-position-x: right;
    background-position-y: bottom;
}

.displayShow >>> .vgt-table th {
    text-align: center;
    text-shadow: -1px -1px 0 rgba(0, 0, 0, 0.3);
    background-color: rgb(51, 51, 51);
    white-space: nowrap;
    color: rgb(255, 255, 255);
    border-collapse: collapse;
    font-weight: normal;
    position: relative;
    background-image: none;
    padding: 4px;
    cursor: default;
}

.displayShow >>> .vgt-table thead th.sortable button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background: transparent;
    border: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.displayShow >>> .vgt-table thead th.sorting {
    background-repeat: no-repeat;
}

.displayShow >>> .vgt-table input.tablesorter-filter {
    width: 98%;
    height: auto;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
}

.displayShow >>> .vgt-table tr.tablesorter-filter-row,
.displayShow >>> .vgt-table tr.tablesorter-filter-row td {
    text-align: center;
}

/* optional disabled input styling */
.displayShow >>> .vgt-table input.tablesorter-filter-row .disabled {
    display: none;
}

.tablesorter-header-inner {
    padding: 0 2px;
    text-align: center;
}

.displayShow >>> .vgt-table tfoot tr {
    color: rgb(255, 255, 255);
    text-align: center;
    text-shadow: -1px -1px 0 rgba(0, 0, 0, 0.3);
    background-color: rgb(51, 51, 51);
    border-collapse: collapse;
}

.displayShow >>> .vgt-table tfoot a {
    color: rgb(255, 255, 255);
    text-decoration: none;
}

.displayShow >>> .vgt-table th.vgt-row-header {
    text-align: left;
}

.displayShow >>> .vgt-table .season-header {
    display: inline;
    margin-left: 5px;
}

.displayShow >>> .vgt-table tr.spacer {
    height: 25px;
}

.displayShow >>> .vgt-table tr:hover {
    opacity: 0.8;
}

.displayShow >>> .unaired {
    background-color: rgb(245, 241, 228);
}

.displayShow >>> .skipped {
    background-color: rgb(190, 222, 237);
}

.displayShow >>> .preferred {
    background-color: rgb(195, 227, 200);
}

.displayShow >>> .archived {
    background-color: rgb(195, 227, 200);
}

.displayShow >>> .allowed {
    background-color: rgb(255, 218, 138);
}

.displayShow >>> .wanted {
    background-color: rgb(255, 176, 176);
}

.displayShow >>> .snatched {
    background-color: rgb(235, 193, 234);
}

.displayShow >>> .downloaded {
    background-color: rgb(255, 218, 138);
}

.displayShow >>> .failed {
    background-color: rgb(255, 153, 153);
}

.displayShow >>> span.unaired {
    color: rgb(88, 75, 32);
}

.displayShow >>> span.skipped {
    color: rgb(29, 80, 104);
}

.displayShow >>> span.preffered {
    color: rgb(41, 87, 48);
}

.displayShow >>> span.allowed {
    color: rgb(118, 81, 0);
}

.displayShow >>> span.wanted {
    color: rgb(137, 0, 0);
}

.displayShow >>> span.snatched {
    color: rgb(101, 33, 100);
}

.displayShow >>> span.unaired b,
.displayShow >>> span.skipped b,
.displayShow >>> span.preferred b,
.displayShow >>> span.allowed b,
.displayShow >>> span.wanted b,
.displayShow >>> span.snatched b {
    color: rgb(0, 0, 0);
    font-weight: 800;
}

.mobile-select {
    width: 110px;
    font-size: x-small;
}

td.col-footer {
    text-align: left !important;
}

.displayShow >>> .vgt-wrap__footer {
    color: rgb(255, 255, 255);
    padding: 1em;
    background-color: rgb(51, 51, 51);
    margin-bottom: 1em;
    display: flex;
    justify-content: space-between;
}

.displayShow >>> .footer__row-count,
.displayShow >>> .footer__navigation__page-info {
    display: inline;
}

.displayShow >>> .footer__row-count__label {
    margin-right: 1em;
}

.displayShow >>> .vgt-wrap__footer .footer__navigation {
    font-size: 14px;
}

.displayShow >>> .vgt-pull-right {
    float: right !important;
}

.displayShow >>> .vgt-wrap__footer .footer__navigation__page-btn .chevron {
    width: 24px;
    height: 24px;
    border-radius: 15%;
    position: relative;
    margin: 0 8px;
}

.displayShow >>> .vgt-wrap__footer .footer__navigation__info,
.displayShow >>> .vgt-wrap__footer .footer__navigation__page-info {
    display: inline-flex;
    color: #909399;
    margin: 0 16px;
    margin-top: 0;
    margin-right: 16px;
    margin-bottom: 0;
    margin-left: 16px;
}

.select-info span {
    margin-left: 5px;
    line-height: 40px;
}

.subtitles > div {
    float: left;
}

.subtitles > div:not(:last-child) {
    margin-right: 2px;
}

.displayShow >>> .vgt-dropdown > .button-group {
    position: relative;
}

.displayShow >>> .dropdown-toggle {
    position: absolute;
    z-index: 1;
    top: 0.1em;
    right: 0.1em;
    width: 1em;
    transition: width 0.2s ease-in-out;
}

.displayShow >>> .dropdown-toggle:hover,
.displayShow >>> .dropdown-toggle:active {
    width: 2em;
}

.displayShow >>> .vgt-dropdown-menu {
    position: absolute;
    z-index: 1;
    float: left;
    min-width: 160px;
    padding: 5px 0;
    margin: 2px 0 0;
    font-size: 14px;
    text-align: left;
    list-style: none;
    background-clip: padding-box;
    border-radius: 3px;
    right: 0;
    top: 2em;
}

.displayShow >>> .vgt-dropdown-menu > li > span {
    display: block;
    padding: 3px 5px;
    clear: both;
    font-weight: 400;
    line-height: 1.42857143;
    white-space: nowrap;
}
</style>
